import { ICabin, ICabinInfoByType, IDeck } from '@interfaces/ship';
import React from 'react';
import styled from '@emotion/styled';
import Container from '@mui/material/Container';
import {
    Accordion,
    AccordionHead,
    AccordionCollapseContent,
} from '../../molecules/accordion/accordion';
import SvgIcon from '@mui/material/SvgIcon';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';

import { StaticPill } from '../../atoms/static-pill/static-pill';
import { objectKeys } from '../../../utils/object-keys';
import { __ } from '../../../utils/translation-service';
import {
    StyledSubCabinContentHeading,
    StyledSubCabinContent,
    StyledSubCabinContentItem,
    StyledCabinTypeSection,
    StyledCabinTitle,
} from './cabin-type.style';

import UserIcon from '../../../../public/static/icons/adults.svg';
import SizeIcon from '../../../../public/static/icons/area.svg';
import StackIcon from '../../../../public/static/icons/stack.svg';
import { colorsUnifications } from '../../../style/global-style';

export interface ICabinTypeProps {
    headline: string;
    description: string;
    cabinTypes: ICabinInfoByType;
    decks: IDeck[];
}

export interface ICabinTypeDetailsProps {
    cabin: ICabin;
    expanded: boolean;
    onToggle: () => void;
}

const StyledStaticPill = styled(StaticPill)`
    border: 2px solid #f0f0f0;
    border-radius: 32px;
    box-shadow: 0px 6px 8px rgba(0, 0, 0, 0.02);
    cursor: pointer;
`;

const CabinInfoIcon = (props: { icon: any; text: string }) => (
    <Box
        component={'span'}
        sx={{ display: 'flex', alignItems: 'center' }}
        color={colorsUnifications.gunmetal[100]}
    >
        <SvgIcon
            inheritViewBox
            style={{
                color: colorsUnifications.gunmetal[70],
                fontSize: 16,
                marginRight: '8px',
            }}
            component={props.icon}
        />
        {props.text}
    </Box>
);

export const CabinTypeDetails = ({ cabin, expanded, onToggle }: ICabinTypeDetailsProps) => {
    return (
        <Accordion expanded={expanded} onChange={onToggle}>
            <AccordionHead id={cabin.title}>
                <StyledCabinTitle>{cabin.title}</StyledCabinTitle>
                <Box
                    component={'p'}
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        gap: '24px',
                        margin: '0',
                    }}
                >
                    <CabinInfoIcon icon={SizeIcon} text={cabin.size} />
                    <CabinInfoIcon icon={UserIcon} text={`max. ${cabin.maxPassengers}`} />
                    <CabinInfoIcon icon={StackIcon} text={cabin.location} />
                </Box>
            </AccordionHead>
            <AccordionCollapseContent>
                <Grid
                    container
                    spacing={2}
                    sx={(theme) => ({
                        [theme.breakpoints.down('md')]: {
                            flexDirection: 'column-reverse',
                        },
                    })}
                >
                    <Grid item xs={12} md={8}>
                        {cabin.amenities.length > 0 && (
                            <>
                                <StyledSubCabinContentHeading>
                                    {__('cabinAmenities')}
                                </StyledSubCabinContentHeading>
                                <StyledSubCabinContent>
                                    {cabin.amenities.map((item, index) =>
                                        item ? (
                                            <StyledSubCabinContentItem
                                                key={`cabin-amenities${index}`}
                                            >
                                                {item}
                                            </StyledSubCabinContentItem>
                                        ) : null,
                                    )}
                                </StyledSubCabinContent>
                            </>
                        )}
                        {cabin.advantages.length > 0 && (
                            <>
                                <StyledSubCabinContentHeading>
                                    {__('cabinAdvantages')}
                                </StyledSubCabinContentHeading>
                                {cabin.advantages.map((item, index) =>
                                    item ? (
                                        <StyledSubCabinContentItem key={`cabin-advantages${index}`}>
                                            {item}
                                        </StyledSubCabinContentItem>
                                    ) : null,
                                )}
                            </>
                        )}
                    </Grid>
                    <Grid item xs={12} md={4}>
                        {cabin?.image?.original && (
                            <Box
                                component={'img'}
                                sx={{ borderRadius: '6px', maxWidth: '100%' }}
                                src={cabin.image.original}
                                alt={cabin.title}
                            />
                        )}
                    </Grid>
                </Grid>
            </AccordionCollapseContent>
        </Accordion>
    );
};

const CABIN_TYPES_ORDER: Array<keyof ICabinInfoByType> = ['inside', 'outside', 'balcony', 'suite'];

export const CabinType = (props: ICabinTypeProps) => {
    const cabinTypesComps = objectKeys(props.cabinTypes).sort((a, b) => {
        return CABIN_TYPES_ORDER.indexOf(a) - CABIN_TYPES_ORDER.indexOf(b);
    });

    const [activeCabinType, setActiveCabinType] = React.useState(cabinTypesComps[0] ?? '');
    const [expandedCabinTitle, setExpandedCabinTitle] = React.useState('');

    const decksByCabinType = React.useMemo(
        () =>
            props.decks.reduce(
                (acc, deck) => {
                    deck.cabins?.forEach((cabin) => {
                        if (!acc[cabin.kind]) {
                            acc[cabin.kind] = [];
                        }

                        const isCabinTypeAlreadyAdded = acc[cabin.kind].some(
                            (el) => el.externalCode === cabin.externalCode,
                        );

                        if (isCabinTypeAlreadyAdded) {
                            return;
                        }

                        acc[cabin.kind].push(cabin);
                    });
                    return acc;
                },
                {} as Record<string, ICabin[]>,
            ),
        props.decks,
    );

    const handleCabinExpand = (cabinTitle: string) => {
        if (expandedCabinTitle === cabinTitle) {
            setExpandedCabinTitle('');
        } else {
            setExpandedCabinTitle(cabinTitle);
        }
    };

    return (
        <Container>
            <StyledCabinTypeSection>
                <h1>{props.headline}</h1>
                <p>{props.description}</p>

                <h3> {__('cabins')} </h3>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        gap: '8px',
                        marginBottom: '36px',
                    }}
                >
                    {cabinTypesComps.map((type) => (
                        <StyledStaticPill
                            key={type}
                            type={activeCabinType === type ? 'primary' : undefined}
                            onClick={() => setActiveCabinType(type)}
                        >
                            {__(type)}
                        </StyledStaticPill>
                    ))}
                </Box>

                {(decksByCabinType[activeCabinType] || []).map((cabin) => (
                    <CabinTypeDetails
                        key={cabin.title}
                        cabin={cabin}
                        expanded={expandedCabinTitle === cabin.title}
                        onToggle={() => handleCabinExpand(cabin.title)}
                    />
                ))}
            </StyledCabinTypeSection>
        </Container>
    );
};
